// AI Controls — owner/admin flips any AI behavior on/off per account.
// Mounted in Settings. Backend at /api/ai-controls. Every AI feature checks
// its corresponding flag before firing (SMS reply, drip, nudges, voice, etc).

import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Spinner } from '../ui.jsx';

export function AiControlsCard({ toast }) {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api('/api/ai-controls').then(r => setFlags(r.flags)).catch(()=>setFlags({}));
  useEffect(() => { load(); }, []);
  if (!flags) return <div style={{ padding:20, textAlign:'center' }}><Spinner sz={20}/></div>;

  const toggle = async (key, enabled) => {
    setBusy(key);
    try {
      await api(`/api/ai-controls/${key}`, { method:'PATCH', body:{ enabled }});
      toast?.(enabled ? `✓ ${flags[key].label} ON` : `${flags[key].label} OFF`, 'success');
      load();
    } catch (e) { toast?.(e.message, 'error'); }
    setBusy(null);
  };

  // Group into sections for scannability.
  const GROUPS = [
    { title: '📱 SMS + Ask AI',    keys: ['sms_ai_reply', 'ask_ai_widget', 'instant_welcome_sms', 'lead_nurture_drip'] },
    { title: '📞 Voice',           keys: ['voice_inbound_answer', 'voice_outbound_book', 'voice_confirm', 'voice_noshow', 'voice_transcript_log', 'voice_self_learning'] },
    { title: '💰 Nudges',          keys: ['nudge_payment', 'nudge_agreement', 'nudge_booking_confirm'] },
    { title: '🌐 Content + Brand', keys: ['blog_auto_generate', 'business_profile_scan'] },
    { title: '🧠 Learning',        keys: ['ai_events_learning', 'coaching_digest'] },
  ];
  return <div>
    <h2 style={{ margin:'0 0 6px', fontSize:20 }}>🎛 AI Controls</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:16, lineHeight:1.5 }}>
      Every AI behavior in your account, on or off. Flip anything you don't want firing. Defaults are on — changes go live immediately for the next scheduled run.
    </div>
    {GROUPS.map(g => (
      <div key={g.title} style={{ ...styles.card, padding:'6px 14px 12px', marginBottom:12 }}>
        <div style={{ fontSize:12, color:G.textMuted, fontWeight:800, textTransform:'uppercase', letterSpacing:'.05em', margin:'12px 0 6px' }}>{g.title}</div>
        {g.keys.map(k => {
          const f = flags[k]; if (!f) return null;
          return <div key={k} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, padding:'10px 0', borderTop:`1px solid ${G.border}` }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:700 }}>{f.label}</div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2, lineHeight:1.4 }}>{f.desc}</div>
            </div>
            <ToggleSwitch on={f.on} disabled={busy === k} onChange={v => toggle(k, v)}/>
          </div>;
        })}
      </div>
    ))}
  </div>;
}

function ToggleSwitch({ on, onChange, disabled }) {
  return <button onClick={() => !disabled && onChange(!on)} disabled={disabled}
    style={{
      width:48, height:26, borderRadius:14, border:'none', cursor: disabled ? 'wait' : 'pointer',
      background: on ? G.green : G.input, position:'relative', flexShrink:0, transition:'background .15s',
    }}>
    <span style={{
      position:'absolute', top:2, left: on ? 24 : 2, width:22, height:22, borderRadius:'50%',
      background:'#fff', transition:'left .15s', boxShadow:'0 1px 3px rgba(0,0,0,.3)',
    }}/>
  </button>;
}
